import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog as MatDialogV2 } from '@angular/material/dialog';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { DsModalService } from '@design-system/ds-base-modal/services/ds-modal.service';
import { first, of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { Constant, RoleName } from 'src/app/@shared/constants/Constant';
import { SplitRecordDialogComponent } from 'src/app/MaterialDialogs/split-record-dialog/split-record-dialog.component';
import { ISplitRecordPopupDataViewModel } from 'src/app/MaterialDialogs/split-record-dialog/split-record-dialog.model';
import { ILoggedInUserViewModel } from 'src/app/model/common.model';
import { CustomerList } from 'src/app/service/customer.model';
import { ExportService } from 'src/app/service/exportCSV.service';
import { filterServiceRequestData } from 'src/app/service/filterData.model';
import { ReportService } from 'src/app/service/report.service';
import { ServiceRequestService } from 'src/app/service/service-request.service';
import { environment } from 'src/environments/environment';
import { IBlockTimeItem, IBlocktimeReportSorting, IBlockTimeRequestResponse, IBlockTimeSums } from './block-time-report.model';
import { NotificationService } from '@notification/service';
import { blockTimeRecordStatusLabels, subTotalReportSum, totalOfBlockTimeReportValues } from './block-time-report.config';
import { MemberService } from '../../service/member.service';
import { canVerifyIbRepo } from 'src/app/pages/v2/reports/ib-repo-verification/helpers/can-verify-ib-repo.helper';
import { IbRepoVerificationDialogComponent } from 'src/app/pages/v2/reports/ib-repo-verification/components/ib-repo-verification-dialog/ib-repo-verification-dialog.component';
import { IbRepoVerificationDialogData } from 'src/app/pages/v2/reports/ib-repo-verification/interfaces/ib-repo-verification-dialog-data.interface';
import { BlockTimeRecordModalComponent } from 'src/app/pages/v2/reports/block-time-record-modal/component/block-time-record-modal.component';
import { BlockTimeRecordModalModeEnum } from 'src/app/pages/v2/reports/block-time-record-modal/enums/block-time-record-modal-mode.enum';
import { BlockTimeRecordModalDataInterface } from 'src/app/pages/v2/reports/block-time-record-modal/interfaces/block-time-record-modal-data.interface';
import { BlockTimeRecordModalResultInterface } from 'src/app/pages/v2/reports/block-time-record-modal/interfaces/block-time-record-modal-result.interface';
import {
  BLOCK_TIME_RECORD_MODAL_MAX_WIDTH,
  BLOCK_TIME_RECORD_MODAL_WIDTH,
} from 'src/app/pages/v2/reports/block-time-record-modal/properties/block-time-record-modal.properties';

declare var $: any;

@Component({
  selector: 'app-block-time-report',
  templateUrl: './block-time-report.component.html',
  styleUrls: ['./block-time-report.component.css'],
})
export class BlockTimeReportComponent implements OnInit, OnDestroy {
  @Input() filterReport: filterServiceRequestData;
  @Input() searchText: string;
  customerList: CustomerList[] = [];
  showAwaitingVerificationOnly = false;

  private _subscriptions: Subscription = new Subscription();

  blockTimeReportData: IBlockTimeItem[] = [];
  blockTimeReportCsvData = [];
  loader: boolean = false;
  timeZone;
  currentPageNumber: number = 1;
  currentPage: number = 1;
  selectedPage: number = 10;
  pagination: string;
  responsive: any;
  sortBy: string;
  sortOrder: boolean;
  blockTimeReportSorting: IBlocktimeReportSorting;
  user: ILoggedInUserViewModel;
  isInternalReadOnly: boolean = false;
  canVerifyIbRepo: boolean = false;
  itineraryPageRedirectUrl = `${environment.redirectURL}`;
  totalOfBlockTimeReportValues = totalOfBlockTimeReportValues;
  totalSum: IBlockTimeSums | null = null;

  constructor(
    private _reportService: ReportService,
    private _exportService: ExportService,
    private _serviceRequestService: ServiceRequestService,
    private _dialog: MatDialog,
    private readonly memberService: MemberService,
    private readonly notificationService: NotificationService,
    private readonly dialogV2: MatDialogV2,
    private readonly dsModalService: DsModalService,
  ) {
    this.user = JSON.parse(localStorage.getItem('user'));
    this.isInternalReadOnly = this.user.role === RoleName.InternalReadOnly;
    this.canVerifyIbRepo = canVerifyIbRepo(this.user.role);

    this._reportService.loaderState.next(true);

    this._subscriptions.add(
      this._serviceRequestService.timZoneChange.subscribe(() => {
        this.timeZone = Constant.selectedTimeZoneShortValue;
      }),
    );

    this._subscriptions.add(
      this._reportService.openAddBlockTimeRecordPopUp.subscribe(() => {
        this.openAddBlockTimeRecordPopUp();
      }),
    );

    this._subscriptions.add(
      this._reportService.filterBlockTimeReportData.pipe(switchMap(() => of(true))).subscribe(() => {
        this.getBlockTimeReportData();
      }),
    );

    this._subscriptions.add(
      this._reportService.showCountForBlockTimeReport.subscribe((response: number) => {
        this.selectedPage = response;
        this.currentPageNumber = 1;
        this.updatePagination();
      }),
    );

    this.initializeBlocktimeReportSorting();
  }

  ngOnInit(): void {
    // The split dialog reads this list.
    this.getCustomers();
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  initializeBlocktimeReportSorting() {
    this.sortBy = 'srCreatedOnDate';
    this.sortOrder = false;
    this.blockTimeReportSorting = {
      srCreatedOnDate: false,
      tripID: false,
      customerName: false,
      serviceType: false,
      additionalOrgans: false,
      originAddress: false,
      destinationAddress: false,
      unosid: false,
      blockTimeRecordStatus: false,
      airVendor: false,
      routing: false,
      tailNumber: false,
      aircraftType: false,
      actualBlockTime: false,
      aircraftSize: false,
      base: false,
      cost: false,
      profit: false,
      rate: false,
      invoiceID: false,
      ar: false,
      ap: false,
      estimatedBlockHours: false,
      groundVendor: false,
      inboundReposition: false,
      outboundReposition: false,
      flightMargin: false,
      ibEstimatedBlockHours: false,
      obEstimatedBlockHours: false,
      liveFlightHours: false,
      cancellationHours: false,
    };
  }

  getBlockTimeReportData() {
    this._reportService.fetchBlockTimeReport(this.filterReport).subscribe({
      next: (response: IBlockTimeRequestResponse) => {
        this.blockTimeReportData =
          response != null
            ? response.blockTimeItems.sort((firstRecord, secondRecord) => {
                const firstDate = new Date(firstRecord.srCreatedOnDate);
                const secondDate = new Date(secondRecord.srCreatedOnDate);
                return Number(firstDate) > Number(secondDate) ? 1 : Number(firstDate) < Number(secondDate) ? -1 : 0;
              })
            : [];
        this.totalSum = response.sums;
        this.currentPageNumber = 1;
        this.initializeBlocktimeReportSorting();
        this._reportService.loaderState.next(false);
        this.updatePagination();
        this.calculateTotalValues();
      },
      error: (error) => {
        console.log(error);
        this.blockTimeReportData = [];
        this.notificationService.warning('Something went wrong while getting blockTime report!');
        this._reportService.loaderState.next(false);
      },
    });
  }

  /** `formattedVerifiedOn` is already rendered in the display timezone (srTimezoneDate in the template). */
  getIbRepoVerifiedTooltip(verifiedByName: string | null | undefined, formattedVerifiedOn: string): string {
    const verifiedBy = verifiedByName ? ` by ${verifiedByName}` : '';
    const verifiedOn = formattedVerifiedOn ? ` on ${formattedVerifiedOn}` : '';

    return `Verified${verifiedBy}${verifiedOn}`;
  }

  openIbRepoVerification(record: IBlockTimeItem): void {
    const data: IbRepoVerificationDialogData = {
      blockTimeRecordId: record.blockTimeReportID,
      tripId: record.tripID,
      reason: record.ibRepoVerificationReason,
      expectedAirport: record.inboundReposition,
      capturedAirport: record.actualInBoundReposition,
      expectedHours: record.ibRepoExpectedHours ?? null,
      capturedHours: record.ibRepoCapturedHours ?? null,
      verifiedHours: record.ibRepoVerifiedHours ?? null,
      verifiedByName: record.ibRepoVerifiedByName ?? null,
      verifiedOn: record.ibRepoVerifiedOn ?? null,
      note: record.ibRepoVerificationNote ?? null,
    };

    this._subscriptions.add(
      this.dialogV2
        .open<IbRepoVerificationDialogComponent, IbRepoVerificationDialogData, boolean>(IbRepoVerificationDialogComponent, {
          data,
          width: '560px',
          autoFocus: false,
        })
        .afterClosed()
        .pipe(first())
        .subscribe((isSaved) => {
          if (isSaved === true) {
            this.getBlockTimeReportData();
          }
        }),
    );
  }

  generateBlockTimeCsvReport(reportName: string) {
    this.prepareBlockTimeReportCsvData();
    this._exportService.exportAsExcelSingleFile(this.blockTimeReportCsvData, reportName);
  }

  prepareBlockTimeReportCsvData() {
    this.blockTimeReportCsvData = [];
    this.blockTimeReportData.forEach((reportData) => {
      this.blockTimeReportCsvData.push({
        Date: this._reportService.convertToMST(reportData.srCreatedOnDate),
        'Trip #': reportData.tripID,
        Customer: reportData.customer.customerName,
        'Service Request Type': reportData.serviceType,
        'Organ Type': reportData.additionalOrgans,
        'Unos ID': reportData.unosid,
        'Block Time Status': reportData.blockTimeRecordStatus
          ? (blockTimeRecordStatusLabels[reportData.blockTimeRecordStatus] ?? reportData.blockTimeRecordStatus)
          : '',
        'SR Status': reportData.serviceRequestStatus ?? '',
        'Ground Vendor': reportData.groundVendor,
        'Air Vendor': reportData.airVendor,
        'OB Repo': reportData.outboundReposition,
        'OB Repo Hours': reportData.obEstimatedBlockHours,
        Routing: reportData.routing,
        'IB Repo': reportData.inboundReposition,
        'IB Repo Hours': reportData.ibEstimatedBlockHours,
        'Tail Number': reportData.tailNumber,
        'Aircraft Types': reportData.aircraftType,
        'Est. Block Hours': reportData.estimatedBlockHours,
        'Actual Block Time': reportData.actualBlockTime,
        'Cancellation Hours': reportData.cancellationHours,
        'Billed Hours': reportData.totalBilledHours,
        'Aircraft Size': reportData.aircraftSize,
        Cost: reportData.cost,
        Profit: reportData.profit,
        Rate: `${reportData.rate != null ? reportData.rate + ' %' : ''}`,
        'Flight Margin': `${reportData.flightMargin != null ? reportData.flightMargin + ' %' : ''}`,
        'Invoice #': reportData.invoiceID,
        'A/R': reportData.ar === true ? 'Yes' : 'No',
        'A/P': reportData.ap === true ? 'Yes' : 'No',
        Note: reportData.note,
      });
    });
  }

  openAddBlockTimeRecordPopUp(): void {
    this.openBlockTimeRecordModal({ mode: BlockTimeRecordModalModeEnum.Add, record: null });
  }

  openEditBlockTimeReport(record: IBlockTimeItem): void {
    this.openBlockTimeRecordModal({ mode: BlockTimeRecordModalModeEnum.Edit, record });
  }

  /**
   * Opens the v2 block time record modal. The grid reloads when the user saved the record or verified IB repo hours.
   */
  private openBlockTimeRecordModal(data: BlockTimeRecordModalDataInterface): void {
    this._subscriptions.add(
      this.dsModalService
        .open<BlockTimeRecordModalComponent, BlockTimeRecordModalDataInterface, BlockTimeRecordModalResultInterface>(
          BlockTimeRecordModalComponent,
          {
            data,
            width: BLOCK_TIME_RECORD_MODAL_WIDTH,
            maxWidth: BLOCK_TIME_RECORD_MODAL_MAX_WIDTH,
            disableClose: true,
          },
        )
        .afterClosed()
        .pipe(first())
        .subscribe((result) => {
          if (result?.isRecordSaved || result?.isIbRepoVerified) {
            this.getBlockTimeReportData();
          }
        }),
    );
  }

  deleteBlockTimeRecord(blockTimeRecordId: string) {
    if (confirm('Do you want to delete the record?')) {
      this._reportService.loaderState.next(true);
      this._subscriptions.add(
        this._subscriptions.add(
          this._reportService.deleteBlockTimeRecord(blockTimeRecordId).subscribe(
            (response) => {
              if (response) {
                this.successToast('Record deleted successfully!');
                this.getBlockTimeReportData();
              } else {
                this.warningToast('Something went wrong while deleting the blocktime record!');
              }
            },
            (error) => {
              console.log(error);
              this.warningToast('Something went wrong while deleting the blocktime record!');
            },
          ),
        ),
      );
    }
  }

  openSplitBlockTimeRecordPopup(record: IBlockTimeItem) {
    const dataToFeed: ISplitRecordPopupDataViewModel = {
      blocktimeRecord: record,
      customerList: this.customerList,
    };
    this._dialog.open(SplitRecordDialogComponent, {
      position: {
        top: '4rem',
      },
      width: '80%',
      height: 'fit-content',
      data: dataToFeed,
    });
  }

  onPageChange() {
    this.updatePagination();
  }

  updatePagination() {
    this.pagination = Constant.showPaginationMessage(this.blockTimeReportData.length, this.currentPageNumber, this.selectedPage);
    this.convertSubTotal();
  }

  calculateTotalValues(): void {
    this.convertSum();
    this.convertSubTotal();
  }

  private convertSubTotal(): void {
    const blockTimeRecordListOfCurrentPage = this.blockTimeReportData.slice(
      (this.currentPageNumber - 1) * this.selectedPage,
      this.selectedPage * this.currentPageNumber,
    );

    this.totalOfBlockTimeReportValues = this.totalOfBlockTimeReportValues.map((item) => {
      if (item.prop === 'count') {
        return {
          ...item,
          subTotal: blockTimeRecordListOfCurrentPage.length,
        };
      }

      if (item.prop === 'sumRate') {
        const costKey = subTotalReportSum['sumCost'];
        const profitKey = subTotalReportSum['sumProfit'];
        const costValue = blockTimeRecordListOfCurrentPage.reduce((acc, curr) => acc + (curr[costKey] ?? 0), 0);
        const profitValue = blockTimeRecordListOfCurrentPage.reduce((acc, curr) => acc + (curr[profitKey] ?? 0), 0);

        const subTotal = this.calculateTotalRate(profitValue, costValue);

        return {
          ...item,
          subTotal,
        };
      }

      if (item.prop === 'sumFlightMargin') {
        const costKey = subTotalReportSum['sumCost'];
        const profitKey = subTotalReportSum['sumProfit'];
        const costValue = blockTimeRecordListOfCurrentPage.reduce((acc, curr) => acc + (curr[costKey] ?? 0), 0);
        const profitValue = blockTimeRecordListOfCurrentPage.reduce((acc, curr) => acc + (curr[profitKey] ?? 0), 0);

        const subTotal = this.calculateTotalFlightMargin(profitValue, costValue);

        return {
          ...item,
          subTotal,
        };
      }

      const responseKey = subTotalReportSum[item.prop];
      const subTotal = responseKey ? blockTimeRecordListOfCurrentPage.reduce((acc, curr) => acc + (curr[responseKey] ?? 0), 0) : 0;
      return {
        ...item,
        subTotal,
      };
    });
  }

  private convertSum(): void {
    this.totalOfBlockTimeReportValues = this.totalOfBlockTimeReportValues.map((item) => {
      return {
        ...item,
        sum: this.totalSum[item.prop] ?? 0,
      };
    });
  }

  updateArOrAp(record: IBlockTimeItem, fieldName: string) {
    this._subscriptions.add(
      this._reportService.updateArOrAP(record).subscribe((response) => {
        if (response) {
          this.successToast(`Value of ${fieldName} is updated successfully.`);
        } else {
          this.warningToast(`Unable to update the value of ${fieldName} !`);
        }
      }),
    );
  }

  warningToast(message: string) {
    this.notificationService.warning(message);
  }

  successToast(message: string) {
    this.notificationService.success(message);
  }

  //TODO: Need to update the logic to show poper using angular approach
  togglePopOver() {
    $(document).ready(function () {
      $('[data-toggle="popover"]').popover();
    });
  }

  /**
   * Resets pagination when the awaiting-verification grid filter is toggled.
   */
  onAwaitingVerificationFilterChange(): void {
    this.currentPageNumber = 1;
    this.updatePagination();
  }

  private getCustomers(): void {
    const customerListSubs = this.memberService
      .getCustomerList()
      .pipe(first())
      .subscribe({
        next: (response: CustomerList[]): void => {
          this.customerList = response;
        },
      });

    this._subscriptions.add(customerListSubs);
  }

  private calculateTotalFlightMargin(profit: number, cost: number): number {
    if (profit > 0 && cost > 0) {
      const flightMargin = (profit / (cost + profit)) * 100;
      return Number(flightMargin.toFixed(2));
    }
    return 0;
  }

  private calculateTotalRate(profit: number, cost: number): number {
    if (profit > 0 && cost > 0) {
      const rate = (profit / cost) * 100;
      return Number(rate.toFixed(2));
    }
    return 0;
  }
}
